import { Editor } from '../core/Editor';
import { ToolType } from '../types';
import { Icons } from './Icons';

export class OptionsBar {
  private editor: Editor;
  private container: HTMLElement;

  constructor(editor: Editor, container: HTMLElement) {
    this.editor = editor;
    this.container = container;
  }

  public render(activeTool: ToolType) {
    this.container.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.className = 'options-bar-inner';

    switch (activeTool) {
      case 'move':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Move & Transform</span>
            <button class="opt-btn" id="btn-align-h" title="Center Horizontally">Center X</button>
            <button class="opt-btn" id="btn-align-v" title="Center Vertically">Center Y</button>
            <button class="opt-btn" id="btn-reset-pos" title="Reset Layer Position">Reset Position</button>
          </div>
        `;
        break;

      case 'marquee':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Marquee Selection</span>
            <button class="opt-btn" id="opt-sel-all">Select All (Ctrl+A)</button>
            <button class="opt-btn" id="opt-sel-inv">Invert Selection</button>
            <button class="opt-btn" id="opt-sel-expand" title="Expand selection by 2px">Expand (+2px)</button>
            <button class="opt-btn" id="opt-sel-contract" title="Contract selection by 2px">Contract (-2px)</button>
            <label class="opt-checkbox" title="Highlight selected pixels with a translucent blue tint">
              <input type="checkbox" id="opt-sel-tint" ${this.editor.selection.showOverlayTint ? 'checked' : ''} />
              <span>Show Tint</span>
            </label>
            <button class="opt-btn" id="opt-sel-desel">Deselect (Ctrl+D)</button>
            <button class="opt-btn danger" id="opt-sel-del">Clear Selection (Del)</button>
          </div>
        `;
        break;

      case 'wand': {
        const isCutCrop = this.editor.toolSettings.wandMode === 'cut-crop';
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Magic Wand</span>
            
            <label class="opt-control" title="Magic wand operational mode">
              <span style="font-weight: 600; color: #4338ca;">Mode:</span>
              <select id="wand-mode-select" class="styled-select" style="font-weight: 600; min-width: 185px;">
                <option value="cut-crop" ${isCutCrop ? 'selected' : ''}>✂️ Remove BG & Crop (Default)</option>
                <option value="select" ${!isCutCrop ? 'selected' : ''}>🪄 Selection Tool (Shift+W)</option>
              </select>
            </label>

            <div class="opt-divider"></div>

            <label class="opt-control" title="Tolerance / threshold of color similarity (0 to 255)">
              <span>Threshold:</span>
              <button class="opt-btn-mini ${this.editor.toolSettings.wandAutoTolerance ? 'active auto-pill' : ''}" id="btn-wand-auto-tol" title="Automatic Threshold Optimizer: Automatically prevents leaking into foreground subjects">⚡ Auto</button>
              <input type="range" id="wand-tol-slider" min="0" max="255" value="${this.editor.toolSettings.wandTolerance}" class="styled-range" style="width: 85px;" />
              <input type="number" id="wand-tol-num" min="0" max="255" value="${this.editor.toolSettings.wandTolerance}" class="styled-num-input" style="width: 44px; text-align: center;" />
            </label>

            <div class="opt-btn-group" title="Quick threshold presets">
              <button class="opt-btn-mini ${!this.editor.toolSettings.wandAutoTolerance && this.editor.toolSettings.wandTolerance === 16 ? 'active' : ''}" data-tol="16">16</button>
              <button class="opt-btn-mini ${!this.editor.toolSettings.wandAutoTolerance && this.editor.toolSettings.wandTolerance === 32 ? 'active' : ''}" data-tol="32">32</button>
              <button class="opt-btn-mini ${!this.editor.toolSettings.wandAutoTolerance && this.editor.toolSettings.wandTolerance === 64 ? 'active' : ''}" data-tol="64">64</button>
              <button class="opt-btn-mini ${!this.editor.toolSettings.wandAutoTolerance && this.editor.toolSettings.wandTolerance === 128 ? 'active' : ''}" data-tol="128">128</button>
            </div>

            <div class="opt-divider"></div>

            <label class="opt-control" title="Padding in pixels preserved around subject when cropping borders">
              <span>Padding:</span>
              <input type="number" id="wand-pad-num" min="0" max="100" value="${this.editor.toolSettings.wandCropPadding || 0}" class="styled-num-input" style="width: 42px; text-align: center;" />
              <span style="font-size: 11px; color: var(--text-muted);">px</span>
            </label>

            <div class="opt-divider"></div>

            <label class="opt-checkbox" title="Select only contiguous connected pixels">
              <input type="checkbox" id="wand-contig" ${this.editor.toolSettings.wandContiguous ? 'checked' : ''} />
              <span>Contiguous</span>
            </label>

            <label class="opt-checkbox" title="Sample all visible layers combined">
              <input type="checkbox" id="wand-sample-all" ${this.editor.wandSampleAll ? 'checked' : ''} />
              <span>Sample All</span>
            </label>

            <div class="opt-divider"></div>

            ${isCutCrop ? `
              <button class="opt-btn primary" id="btn-wand-autocrop" title="1-Click automatic background removal & border trimming" style="font-weight: 600; background: linear-gradient(135deg, #4f46e5, #0284c7); color: white; border: none;">
                ✨ 1-Click Remove BG & Crop
              </button>
              <button class="opt-btn" id="btn-trim-borders" title="Auto-crop all transparent borders right now (Alt+Shift+T)">
                📐 Trim Borders
              </button>
            ` : `
              <label class="opt-checkbox" title="Highlight selected pixels with translucent tint">
                <input type="checkbox" id="wand-tint-toggle" ${this.editor.selection.showOverlayTint ? 'checked' : ''} />
                <span>Show Tint</span>
              </label>
              <button class="opt-btn" id="opt-wand-expand" title="Expand selection boundary outward (+2px)">Expand (+2px)</button>
              <button class="opt-btn" id="opt-wand-contract" title="Contract selection boundary inward (-2px)">Contract (-2px)</button>
              <button class="opt-btn" id="opt-wand-invert" title="Invert selection (Ctrl+Shift+I)">Invert</button>
              <button class="opt-btn" id="opt-wand-desel" title="Deselect (Ctrl+D)">Deselect</button>
              <button class="opt-btn danger" id="opt-wand-clear" title="Clear selected pixels to transparent (Delete)">Clear (Del)</button>
              <button class="opt-btn" id="btn-trim-borders" title="Auto-crop all transparent borders right now (Alt+Shift+T)">📐 Trim Borders</button>
            `}
          </div>
        `;
        break;
      }

      case 'crop':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Crop Aspect Ratio:</span>
            <select id="crop-aspect" class="styled-select mini">
              <option value="free" ${this.editor.toolSettings.cropAspect === 'free' ? 'selected' : ''}>Freeform</option>
              <option value="1:1" ${this.editor.toolSettings.cropAspect === '1:1' ? 'selected' : ''}>1:1 Square</option>
              <option value="4:3" ${this.editor.toolSettings.cropAspect === '4:3' ? 'selected' : ''}>4:3 Standard</option>
              <option value="16:9" ${this.editor.toolSettings.cropAspect === '16:9' ? 'selected' : ''}>16:9 Landscape</option>
              <option value="9:16" ${this.editor.toolSettings.cropAspect === '9:16' ? 'selected' : ''}>9:16 Story/Reel</option>
              <option value="3:2" ${this.editor.toolSettings.cropAspect === '3:2' ? 'selected' : ''}>3:2 Photo</option>
            </select>
            <button class="opt-btn primary" id="btn-crop-apply">Apply Crop (Enter)</button>
            <button class="opt-btn" id="btn-crop-cancel">Cancel (Esc)</button>
          </div>
        `;
        break;

      case 'brush':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Brush</span>
            <label class="opt-control">
              <span>Size:</span>
              <input type="range" id="brush-size" min="1" max="150" value="${this.editor.toolSettings.brushSize}" class="styled-range mini" />
              <span class="opt-val" id="brush-size-val">${this.editor.toolSettings.brushSize}px</span>
            </label>
            <label class="opt-control">
              <span>Hardness:</span>
              <input type="range" id="brush-hard" min="0" max="100" value="${this.editor.toolSettings.brushHardness}" class="styled-range mini" />
              <span class="opt-val" id="brush-hard-val">${this.editor.toolSettings.brushHardness}%</span>
            </label>
            <label class="opt-control">
              <span>Opacity:</span>
              <input type="range" id="brush-opac" min="1" max="100" value="${this.editor.toolSettings.brushOpacity}" class="styled-range mini" />
              <span class="opt-val" id="brush-opac-val">${this.editor.toolSettings.brushOpacity}%</span>
            </label>
          </div>
        `;
        break;

      case 'eraser':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Eraser</span>
            <label class="opt-control">
              <span>Size:</span>
              <input type="range" id="eraser-size" min="2" max="200" value="${this.editor.toolSettings.eraserSize}" class="styled-range mini" />
              <span class="opt-val" id="eraser-size-val">${this.editor.toolSettings.eraserSize}px</span>
            </label>
            <label class="opt-control">
              <span>Hardness:</span>
              <input type="range" id="eraser-hard" min="0" max="100" value="${this.editor.toolSettings.eraserHardness}" class="styled-range mini" />
              <span class="opt-val" id="eraser-hard-val">${this.editor.toolSettings.eraserHardness}%</span>
            </label>
          </div>
        `;
        break;

      case 'smudge':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Smudge</span>
            <label class="opt-control">
              <span>Strength:</span>
              <input type="range" id="smudge-str" min="5" max="100" value="${this.editor.toolSettings.smudgeStrength}" class="styled-range mini" />
              <span class="opt-val" id="smudge-str-val">${this.editor.toolSettings.smudgeStrength}%</span>
            </label>
            <label class="opt-control">
              <span>Radius:</span>
              <input type="range" id="smudge-rad" min="4" max="100" value="${this.editor.toolSettings.smudgeRadius}" class="styled-range mini" />
              <span class="opt-val" id="smudge-rad-val">${this.editor.toolSettings.smudgeRadius}px</span>
            </label>
          </div>
        `;
        break;

      case 'heal':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Repair & Touch Up</span>
            <select id="heal-mode" class="styled-select mini">
              <option value="blemish" ${this.editor.toolSettings.healingMode === 'blemish' ? 'selected' : ''}>Spot Healing (Blemish Remover)</option>
              <option value="clone" ${this.editor.toolSettings.healingMode === 'clone' ? 'selected' : ''}>Clone Stamp (Alt+Click to sample)</option>
            </select>
            <label class="opt-control">
              <span>Radius:</span>
              <input type="range" id="heal-rad" min="3" max="80" value="${this.editor.toolSettings.healingRadius}" class="styled-range mini" />
              <span class="opt-val" id="heal-rad-val">${this.editor.toolSettings.healingRadius}px</span>
            </label>
            <span class="opt-hint">${this.editor.toolSettings.healingMode === 'clone' ? 'Alt+Click canvas to set source' : 'Click blemishes or scratches to heal'}</span>
          </div>
        `;
        break;

      case 'bucket':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Paint Bucket</span>
            <label class="opt-control">
              <span>Tolerance:</span>
              <input type="range" id="bucket-tol" min="0" max="100" value="${this.editor.toolSettings.bucketTolerance}" class="styled-range mini" />
              <span class="opt-val" id="bucket-tol-val">${this.editor.toolSettings.bucketTolerance}</span>
            </label>
            <label class="opt-checkbox">
              <input type="checkbox" id="bucket-contig" ${this.editor.toolSettings.bucketContiguous ? 'checked' : ''} />
              <span>Contiguous</span>
            </label>
          </div>
        `;
        break;

      case 'bg-remove':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Background Remover</span>
            <button class="opt-btn primary" id="btn-auto-bg-rem" title="1-Click Auto Background Removal with Smart Edge Protection" style="background: linear-gradient(135deg, #4f46e5, #0284c7); color: white; border: none; font-weight: 600;">
              ✨ 1-Click Auto Remove
            </button>
            <div class="opt-divider"></div>
            <label class="opt-control" title="Tolerance threshold with automatic edge barrier optimizer">
              <span>Tolerance:</span>
              <button class="opt-btn-mini ${this.editor.toolSettings.bgRemoveAutoTolerance ? 'active auto-pill' : ''}" id="btn-bg-auto-tol" title="Automatic Threshold Optimizer: Protects foreground objects">⚡ Auto</button>
              <input type="range" id="bg-tol" min="5" max="100" value="${this.editor.toolSettings.bgRemoveTolerance || 38}" class="styled-range mini" />
              <span class="opt-val" id="bg-tol-val">${this.editor.toolSettings.bgRemoveAutoTolerance ? 'Auto' : (this.editor.toolSettings.bgRemoveTolerance || 38)}</span>
            </label>
            <div class="opt-divider"></div>
            <button class="opt-btn" id="btn-bg-trim" title="Trim transparent border whitespace">📐 Trim Borders</button>
            <span class="opt-hint">Click anywhere on background to sample & erase</span>
          </div>
        `;
        break;

      case 'text':
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">Text Layer</span>
            <select id="text-font" class="styled-select mini">
              <option value="Inter">Inter</option>
              <option value="Roboto">Roboto</option>
              <option value="Montserrat">Montserrat</option>
              <option value="Playfair Display">Playfair Display</option>
              <option value="Impact">Impact</option>
              <option value="Courier New">Monospace</option>
            </select>
            <label class="opt-control">
              <span>Size:</span>
              <input type="number" id="text-size" min="8" max="250" value="${this.editor.toolSettings.textFontSize}" class="styled-input mini" style="width: 55px;" />
            </label>
            <button class="opt-btn ${this.editor.toolSettings.textBold ? 'active' : ''}" id="btn-text-bold"><strong>B</strong></button>
            <button class="opt-btn ${this.editor.toolSettings.textItalic ? 'active' : ''}" id="btn-text-italic"><em>I</em></button>
            <input type="color" id="text-color" value="${this.editor.toolSettings.textColor}" class="color-swatch-input" title="Text Color" />
            <button class="opt-btn primary" id="btn-add-text">+ Add Text</button>
          </div>
        `;
        break;

      default:
        wrapper.innerHTML = `
          <div class="opt-group">
            <span class="opt-label">${activeTool.toUpperCase()} Tool</span>
            <span class="opt-hint">Select a layer or click canvas to interact</span>
          </div>
        `;
        break;
    }

    this.container.appendChild(wrapper);
    this.attachEvents(activeTool);
  }

  private attachEvents(activeTool: ToolType) {
    const s = this.editor.toolSettings;

    // Move tool
    if (activeTool === 'move') {
      this.container.querySelector('#btn-align-h')?.addEventListener('click', () => {
        const l = this.editor.getActiveLayer();
        if (l) {
          l.x = Math.round((this.editor.width - l.width) / 2);
          this.editor.render();
          this.editor.recordHistory('Center Horizontally');
        }
      });
      this.container.querySelector('#btn-align-v')?.addEventListener('click', () => {
        const l = this.editor.getActiveLayer();
        if (l) {
          l.y = Math.round((this.editor.height - l.height) / 2);
          this.editor.render();
          this.editor.recordHistory('Center Vertically');
        }
      });
      this.container.querySelector('#btn-reset-pos')?.addEventListener('click', () => {
        const l = this.editor.getActiveLayer();
        if (l) {
          l.x = 0;
          l.y = 0;
          this.editor.render();
          this.editor.recordHistory('Reset Layer Position');
        }
      });
    }

    // Marquee tool
    if (activeTool === 'marquee') {
      this.container.querySelector('#opt-sel-all')?.addEventListener('click', () => {
        this.editor.selection.selectAll(this.editor.width, this.editor.height);
        this.editor.render();
      });
      this.container.querySelector('#opt-sel-inv')?.addEventListener('click', () => {
        this.editor.selection.invert(this.editor.width, this.editor.height);
        this.editor.render();
      });
      this.container.querySelector('#opt-sel-expand')?.addEventListener('click', () => {
        this.editor.expandSelection(2);
      });
      this.container.querySelector('#opt-sel-contract')?.addEventListener('click', () => {
        this.editor.contractSelection(2);
      });
      const marqueeTint = this.container.querySelector('#opt-sel-tint') as HTMLInputElement;
      marqueeTint?.addEventListener('change', () => {
        this.editor.selection.showOverlayTint = marqueeTint.checked;
        this.editor.renderOverlayOnly();
      });
      this.container.querySelector('#opt-sel-desel')?.addEventListener('click', () => {
        this.editor.selection.deselect();
        this.editor.render();
      });
      this.container.querySelector('#opt-sel-del')?.addEventListener('click', () => {
        const l = this.editor.getActiveLayer();
        if (l) {
          this.editor.selection.clearSelection(l);
          this.editor.recordHistory('Clear Selection');
          this.editor.render();
        }
      });
    }

    // Magic wand
    if (activeTool === 'wand') {
      const tolSlider = this.container.querySelector('#wand-tol-slider') as HTMLInputElement;
      const tolNum = this.container.querySelector('#wand-tol-num') as HTMLInputElement;
      const autoTolBtn = this.container.querySelector('#btn-wand-auto-tol');

      autoTolBtn?.addEventListener('click', () => {
        this.editor.toggleWandAutoTolerance();
        this.render('wand');
      });

      const updateTolerance = (newTol: number, recordHistory: boolean = false) => {
        s.wandAutoTolerance = false;
        autoTolBtn?.classList.remove('active', 'auto-pill');
        s.wandTolerance = Math.max(0, Math.min(255, newTol));
        if (tolSlider) tolSlider.value = String(s.wandTolerance);
        if (tolNum) tolNum.value = String(s.wandTolerance);

        // Update active preset button highlight
        this.container.querySelectorAll('.opt-btn-mini[data-tol]').forEach(btn => {
          btn.classList.toggle('active', (btn as HTMLElement).dataset.tol === String(s.wandTolerance));
        });

        // Live reapplication if there was a wand click!
        if (this.editor.lastWandPoint) {
          this.editor.reapplyMagicWand(recordHistory);
        }
      };

      tolSlider?.addEventListener('input', () => {
        updateTolerance(parseInt(tolSlider.value) || 0, false);
      });
      tolSlider?.addEventListener('change', () => {
        updateTolerance(parseInt(tolSlider.value) || 0, true);
      });

      tolNum?.addEventListener('input', () => {
        updateTolerance(parseInt(tolNum.value) || 0, false);
      });
      tolNum?.addEventListener('change', () => {
        updateTolerance(parseInt(tolNum.value) || 0, true);
      });

      this.container.querySelectorAll('.opt-btn-mini[data-tol]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const val = parseInt((e.currentTarget as HTMLElement).dataset.tol || '32');
          updateTolerance(val, true);
        });
      });

      const contigBox = this.container.querySelector('#wand-contig') as HTMLInputElement;
      contigBox?.addEventListener('change', () => {
        s.wandContiguous = contigBox.checked;
        if (this.editor.lastWandPoint) {
          this.editor.reapplyMagicWand(true);
        }
      });

      const sampleAllBox = this.container.querySelector('#wand-sample-all') as HTMLInputElement;
      sampleAllBox?.addEventListener('change', () => {
        this.editor.wandSampleAll = sampleAllBox.checked;
        if (this.editor.lastWandPoint) {
          this.editor.reapplyMagicWand(true);
        }
      });

      const wandTint = this.container.querySelector('#wand-tint-toggle') as HTMLInputElement;
      wandTint?.addEventListener('change', () => {
        this.editor.selection.showOverlayTint = wandTint.checked;
        this.editor.renderOverlayOnly();
      });

      this.container.querySelector('#opt-wand-expand')?.addEventListener('click', () => {
        this.editor.expandSelection(2);
      });

      this.container.querySelector('#opt-wand-contract')?.addEventListener('click', () => {
        this.editor.contractSelection(2);
      });

      this.container.querySelector('#opt-wand-clear')?.addEventListener('click', () => {
        const l = this.editor.getActiveLayer();
        if (l) {
          this.editor.selection.clearSelection(l);
          this.editor.recordHistory('Clear Wand Selection');
          this.editor.render();
        }
      });

      this.container.querySelector('#opt-wand-invert')?.addEventListener('click', () => {
        this.editor.selection.invert(this.editor.width, this.editor.height);
        this.editor.render();
      });

      // Wand Mode select
      const modeSelect = this.container.querySelector('#wand-mode-select') as HTMLSelectElement;
      modeSelect?.addEventListener('change', () => {
        const val = modeSelect.value as 'select' | 'cut-crop';
        this.editor.setWandMode(val);
        this.render('wand');
      });

      // Padding input
      const padNum = this.container.querySelector('#wand-pad-num') as HTMLInputElement;
      padNum?.addEventListener('change', () => {
        s.wandCropPadding = Math.max(0, parseInt(padNum.value) || 0);
      });

      // 1-Click Auto Remove BG & Crop
      this.container.querySelector('#btn-wand-autocrop')?.addEventListener('click', () => {
        this.editor.autoRemoveBgAndCrop(s.wandTolerance, s.wandCropPadding || 0);
      });

      // Trim Borders
      this.container.querySelector('#btn-trim-borders')?.addEventListener('click', () => {
        this.editor.trimTransparentBorders(s.wandCropPadding || 0);
      });

      this.container.querySelector('#opt-wand-desel')?.addEventListener('click', () => {
        this.editor.selection.deselect();
        this.editor.render();
      });
    }

    // Crop tool
    if (activeTool === 'crop') {
      const aspectSelect = this.container.querySelector('#crop-aspect') as HTMLSelectElement;
      aspectSelect?.addEventListener('change', () => {
        s.cropAspect = aspectSelect.value as any;
        const cropTool = this.editor.toolManager.getTool('crop') as any;
        if (cropTool && cropTool.applyAspectRatio) {
          cropTool.applyAspectRatio();
          this.editor.renderOverlayOnly();
        }
      });

      this.container.querySelector('#btn-crop-apply')?.addEventListener('click', () => {
        const cropTool = this.editor.toolManager.getTool('crop') as any;
        if (cropTool) cropTool.applyCrop();
      });

      this.container.querySelector('#btn-crop-cancel')?.addEventListener('click', () => {
        const cropTool = this.editor.toolManager.getTool('crop') as any;
        if (cropTool) cropTool.cancelCrop();
      });
    }

    // Brush tool
    if (activeTool === 'brush') {
      const bSize = this.container.querySelector('#brush-size') as HTMLInputElement;
      const bSizeVal = this.container.querySelector('#brush-size-val') as HTMLElement;
      bSize?.addEventListener('input', () => {
        s.brushSize = parseInt(bSize.value);
        if (bSizeVal) bSizeVal.textContent = `${s.brushSize}px`;
      });

      const bHard = this.container.querySelector('#brush-hard') as HTMLInputElement;
      const bHardVal = this.container.querySelector('#brush-hard-val') as HTMLElement;
      bHard?.addEventListener('input', () => {
        s.brushHardness = parseInt(bHard.value);
        if (bHardVal) bHardVal.textContent = `${s.brushHardness}%`;
      });

      const bOpac = this.container.querySelector('#brush-opac') as HTMLInputElement;
      const bOpacVal = this.container.querySelector('#brush-opac-val') as HTMLElement;
      bOpac?.addEventListener('input', () => {
        s.brushOpacity = parseInt(bOpac.value);
        if (bOpacVal) bOpacVal.textContent = `${s.brushOpacity}%`;
      });
    }

    // Eraser tool
    if (activeTool === 'eraser') {
      const eSize = this.container.querySelector('#eraser-size') as HTMLInputElement;
      const eSizeVal = this.container.querySelector('#eraser-size-val') as HTMLElement;
      eSize?.addEventListener('input', () => {
        s.eraserSize = parseInt(eSize.value);
        if (eSizeVal) eSizeVal.textContent = `${s.eraserSize}px`;
      });

      const eHard = this.container.querySelector('#eraser-hard') as HTMLInputElement;
      const eHardVal = this.container.querySelector('#eraser-hard-val') as HTMLElement;
      eHard?.addEventListener('input', () => {
        s.eraserHardness = parseInt(eHard.value);
        if (eHardVal) eHardVal.textContent = `${s.eraserHardness}%`;
      });
    }

    // Smudge tool
    if (activeTool === 'smudge') {
      const sStr = this.container.querySelector('#smudge-str') as HTMLInputElement;
      const sStrVal = this.container.querySelector('#smudge-str-val') as HTMLElement;
      sStr?.addEventListener('input', () => {
        s.smudgeStrength = parseInt(sStr.value);
        if (sStrVal) sStrVal.textContent = `${s.smudgeStrength}%`;
      });

      const sRad = this.container.querySelector('#smudge-rad') as HTMLInputElement;
      const sRadVal = this.container.querySelector('#smudge-rad-val') as HTMLElement;
      sRad?.addEventListener('input', () => {
        s.smudgeRadius = parseInt(sRad.value);
        if (sRadVal) sRadVal.textContent = `${s.smudgeRadius}px`;
      });
    }

    // Healing tool
    if (activeTool === 'heal') {
      const hMode = this.container.querySelector('#heal-mode') as HTMLSelectElement;
      hMode?.addEventListener('change', () => {
        s.healingMode = hMode.value as any;
        this.render('heal');
      });

      const hRad = this.container.querySelector('#heal-rad') as HTMLInputElement;
      const hRadVal = this.container.querySelector('#heal-rad-val') as HTMLElement;
      hRad?.addEventListener('input', () => {
        s.healingRadius = parseInt(hRad.value);
        if (hRadVal) hRadVal.textContent = `${s.healingRadius}px`;
      });
    }

    // Bucket tool
    if (activeTool === 'bucket') {
      const bTol = this.container.querySelector('#bucket-tol') as HTMLInputElement;
      const bTolVal = this.container.querySelector('#bucket-tol-val') as HTMLElement;
      bTol?.addEventListener('input', () => {
        s.bucketTolerance = parseInt(bTol.value);
        if (bTolVal) bTolVal.textContent = s.bucketTolerance.toString();
      });

      const bContig = this.container.querySelector('#bucket-contig') as HTMLInputElement;
      bContig?.addEventListener('change', () => {
        s.bucketContiguous = bContig.checked;
      });
    }

    // BG remove
    if (activeTool === 'bg-remove') {
      const autoTolBtn = this.container.querySelector('#btn-bg-auto-tol');
      const tolSlider = this.container.querySelector('#bg-tol') as HTMLInputElement;
      const tolVal = this.container.querySelector('#bg-tol-val') as HTMLElement;

      autoTolBtn?.addEventListener('click', () => {
        s.bgRemoveAutoTolerance = !s.bgRemoveAutoTolerance;
        this.render('bg-remove');
        this.editor.showToast(s.bgRemoveAutoTolerance ? '⚡ Auto Threshold Optimizer: ON' : 'Manual Tolerance: ON');
      });

      tolSlider?.addEventListener('input', () => {
        s.bgRemoveAutoTolerance = false;
        s.bgRemoveTolerance = parseInt(tolSlider.value) || 38;
        if (tolVal) tolVal.textContent = String(s.bgRemoveTolerance);
        autoTolBtn?.classList.remove('active', 'auto-pill');
      });

      this.container.querySelector('#btn-auto-bg-rem')?.addEventListener('click', () => {
        this.editor.autoRemoveBackground();
      });

      this.container.querySelector('#btn-bg-trim')?.addEventListener('click', () => {
        this.editor.trimTransparentBorders();
      });
    }

    // Text tool
    if (activeTool === 'text') {
      const fontSel = this.container.querySelector('#text-font') as HTMLSelectElement;
      fontSel?.addEventListener('change', () => {
        s.textFontFamily = fontSel.value;
        const l = this.editor.getActiveLayer();
        if (l && l.type === 'text' && l.textData) {
          l.textData.fontFamily = fontSel.value;
          l.renderText();
          this.editor.render();
        }
      });

      const sizeInput = this.container.querySelector('#text-size') as HTMLInputElement;
      sizeInput?.addEventListener('input', () => {
        s.textFontSize = parseInt(sizeInput.value) || 48;
        const l = this.editor.getActiveLayer();
        if (l && l.type === 'text' && l.textData) {
          l.textData.fontSize = s.textFontSize;
          l.renderText();
          this.editor.render();
        }
      });

      const btnBold = this.container.querySelector('#btn-text-bold') as HTMLElement;
      btnBold?.addEventListener('click', () => {
        s.textBold = !s.textBold;
        btnBold.classList.toggle('active', s.textBold);
        const l = this.editor.getActiveLayer();
        if (l && l.type === 'text' && l.textData) {
          l.textData.bold = s.textBold;
          l.renderText();
          this.editor.render();
        }
      });

      const btnItalic = this.container.querySelector('#btn-text-italic') as HTMLElement;
      btnItalic?.addEventListener('click', () => {
        s.textItalic = !s.textItalic;
        btnItalic.classList.toggle('active', s.textItalic);
        const l = this.editor.getActiveLayer();
        if (l && l.type === 'text' && l.textData) {
          l.textData.italic = s.textItalic;
          l.renderText();
          this.editor.render();
        }
      });

      const colorInput = this.container.querySelector('#text-color') as HTMLInputElement;
      colorInput?.addEventListener('input', () => {
        s.textColor = colorInput.value;
        const l = this.editor.getActiveLayer();
        if (l && l.type === 'text' && l.textData) {
          l.textData.color = s.textColor;
          l.renderText();
          this.editor.render();
        }
      });

      this.container.querySelector('#btn-add-text')?.addEventListener('click', () => {
        const text = window.prompt('Enter text:', 'New Text');
        if (text) {
          this.editor.addTextLayer(text, 100, 100, s.textFontFamily, s.textFontSize, s.textColor);
          this.editor.recordHistory('Add Text Layer');
          this.editor.render();
        }
      });
    }
  }
}
